// Full Platform Terms of Service & Facilitator Operating Model Modal
import { getCachedTermsConfig, acceptTerms } from '../services/termsService';
import { showToast } from '../components/Toast';
import type { TermsConfig } from '../types';

export function renderLegalTermsModal(isMandatory: boolean = false, customTerms?: TermsConfig): string {
  const terms = customTerms || getCachedTermsConfig();

  return `
    <div id="modal-legal-terms" class="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto modal-backdrop animate-fade-in" data-mandatory="${isMandatory}">
      <div class="bg-[#0a0c13] border border-cyan-400/40 shadow-[0_0_35px_rgba(0,240,255,0.15)] rounded-2xl max-w-2xl w-full p-5 sm:p-6 space-y-4 my-8 text-xs max-h-[88vh] overflow-y-auto relative">
        <div class="laser-line-cyan absolute top-0 left-0 right-0"></div>
        
        <!-- Header -->
        <div class="flex items-center justify-between border-b border-white/[0.08] pb-3">
          <div class="flex items-center gap-2.5">
            <div class="w-9 h-9 rounded-xl bg-transparent border border-cyan-400/50 text-cyan-400 flex items-center justify-center font-bold shadow-[0_0_12px_rgba(0,240,255,0.2)]">
              <i class="fa-solid fa-scale-balanced"></i>
            </div>
            <div>
              <div class="flex items-center gap-2">
                <h3 class="text-base font-black text-slate-100 tracking-tight">${terms.title}</h3>
                <span class="text-[10px] font-mono text-cyan-400 border border-cyan-400/40 px-1.5 py-0.5 rounded shadow-[0_0_8px_rgba(0,240,255,0.15)]">v${terms.version}</span>
              </div>
              <p class="text-[11px] text-slate-400">Effective ${terms.lastUpdated} &bull; Facilitator noticeboard operating model</p>
            </div>
          </div>
          ${!isMandatory ? `
            <button id="close-legal-modal-btn" class="text-slate-400 hover:text-slate-200 p-1">
              <i class="fa-solid fa-xmark text-lg"></i>
            </button>
          ` : `
            <span class="text-[10px] uppercase font-bold text-pink-400 border border-pink-500/40 px-2 py-0.5 rounded-full shadow-[0_0_8px_rgba(255,42,133,0.3)]">
              Acceptance Required
            </span>
          `}
        </div>

        <!-- Summary Banner & Link to Full Terms and Privacy Page -->
        <div class="p-3 bg-[#06070a] rounded-xl border border-white/[0.08] text-slate-300 text-[11px] leading-relaxed">
          <i class="fa-solid fa-circle-info text-cyan-400 mr-1.5"></i>
          ${terms.summary}
        </div>

        <div class="flex items-center justify-between p-3 rounded-xl bg-cyan-950/20 border border-cyan-400/25 text-slate-300 text-[11px] flex-wrap gap-2">
          <div class="flex items-center gap-2">
            <i class="fa-solid fa-file-contract text-cyan-400"></i>
            <span>Review our complete, comprehensive documentation:</span>
          </div>
          <a href="/terms" target="_blank" class="text-cyan-400 font-bold hover:underline inline-flex items-center gap-1">
            Full list of Terms and Privacy <i class="fa-solid fa-arrow-up-right-from-square text-[10px]"></i>
          </a>
        </div>

        <!-- Terms Sections -->
        <div class="space-y-4 text-slate-300 leading-relaxed text-[11px]">
          ${terms.sections.map(section => `
            <div class="p-3 rounded-xl border border-white/[0.06] bg-[#06070a]/40">
              <h4 class="font-bold text-cyan-300 text-xs uppercase tracking-wider mb-1">${section.title}</h4>
              <p class="text-slate-400 leading-relaxed">${section.content}</p>
            </div>
          `).join('')}
        </div>

        ${isMandatory ? `
          <!-- Mandatory Clickwrap Waiver & Media Release -->
          <div class="space-y-2.5">
            <!-- Checkbox 1: Terms of Service -->
            <div class="p-3.5 bg-transparent border border-white/[0.1] hover:border-pink-500/40 rounded-xl space-y-1 transition-colors relative overflow-hidden">
              <div class="laser-line-pink absolute top-0 left-0 right-0 opacity-40"></div>
              <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" id="accept-terms-checkbox" class="mt-0.5 h-4 w-4 rounded border-white/20 text-pink-500 focus:ring-0 accent-pink-500 bg-[#06070a]">
                <div class="text-[11px] text-slate-200 leading-snug">
                  <span class="font-bold text-slate-100 block mb-0.5">Platform Scope & Terms of Service (v${terms.version}) <span class="text-pink-400">*</span></span>
                  I have read and voluntarily agree to the <a href="/terms#terms-scope" target="_blank" class="text-cyan-400 font-bold underline hover:text-cyan-300">Pops Culture Platform Scope &amp; Terms of Service (v${terms.version})</a>. I acknowledge that gatherings are independent voluntary undertakings and agree to hold harmless Pops Culture from any injury, damage, or dispute.
                </div>
              </label>
            </div>

            <!-- Checkbox 2: Event Media Release & Guest Authorization -->
            <div class="p-3.5 bg-transparent border border-white/[0.1] hover:border-pink-500/40 rounded-xl space-y-1 transition-colors relative overflow-hidden">
              <div class="laser-line-pink absolute top-0 left-0 right-0 opacity-40"></div>
              <label class="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" id="accept-media-checkbox" class="mt-0.5 h-4 w-4 rounded border-white/20 text-pink-500 focus:ring-0 accent-pink-500 bg-[#06070a]">
                <div class="text-[11px] text-slate-200 leading-snug">
                  <span class="font-bold text-slate-100 block mb-0.5">Event Media Release & Guest Authorization <span class="text-pink-400">*</span></span>
                  I agree, on behalf of myself and all family members, minors, or guests attending with me, to the <a href="/terms#media-release" target="_blank" class="text-pink-400 font-bold underline hover:text-pink-300">Media &amp; Photography Release Terms</a>. I grant Pops Culture the right to capture and use our photos, video, and likeness for website, community, and marketing purposes as described, and I warrant that I have the legal authority to grant this permission for all attendees in my party.
                </div>
              </label>
            </div>
          </div>

          <div class="pt-3 border-t border-white/[0.08] flex flex-col sm:flex-row items-center justify-between gap-3">
            <div class="text-[10px] text-slate-400">
              Both acknowledgments are mandatory before accessing Pops Culture.
            </div>
            <button id="accept-terms-submit-btn" disabled class="btn-primary opacity-50 cursor-not-allowed px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-2">
              <i class="fa-solid fa-check"></i> Agree & Continue
            </button>
          </div>
        ` : `
          <!-- Voluntary Close -->
          <div class="pt-3 border-t border-white/[0.08] flex items-center justify-between">
            <span class="text-[10px] text-slate-500">Pops Culture &copy; 2026. All volunteer participation at own risk.</span>
            <button id="acknowledge-legal-btn" class="btn-secondary px-5 py-2 rounded-xl font-bold">
              Close
            </button>
          </div>
        `}

      </div>
    </div>
  `;
}

export function bindLegalTermsModalEvents(
  onClose: () => void,
  onAccepted?: (version: string) => void
): void {
  const modal = document.getElementById('modal-legal-terms');
  const isMandatory = modal?.getAttribute('data-mandatory') === 'true';

  if (!isMandatory) {
    document.getElementById('close-legal-modal-btn')?.addEventListener('click', () => {
      onClose();
    });
    document.getElementById('acknowledge-legal-btn')?.addEventListener('click', () => {
      onClose();
    });

    modal?.addEventListener('click', (e) => {
      if (e.target === modal) {
        onClose();
      }
    });
  } else {
    const termsCheck = document.getElementById('accept-terms-checkbox') as HTMLInputElement;
    const mediaCheck = document.getElementById('accept-media-checkbox') as HTMLInputElement;
    const submitBtn = document.getElementById('accept-terms-submit-btn') as HTMLButtonElement;

    const updateSubmitState = () => {
      const bothChecked = Boolean(termsCheck?.checked && mediaCheck?.checked);
      if (bothChecked) {
        submitBtn.removeAttribute('disabled');
        submitBtn.classList.remove('opacity-50', 'cursor-not-allowed');
      } else {
        submitBtn.setAttribute('disabled', 'true');
        submitBtn.classList.add('opacity-50', 'cursor-not-allowed');
      }
    };

    termsCheck?.addEventListener('change', updateSubmitState);
    mediaCheck?.addEventListener('change', updateSubmitState);

    submitBtn?.addEventListener('click', async () => {
      if (!termsCheck?.checked || !mediaCheck?.checked) return;
      submitBtn.setAttribute('disabled', 'true');
      submitBtn.innerHTML = '<i class="fa-solid fa-spinner fa-spin"></i> Saving...';
      const terms = getCachedTermsConfig();
      try {
        await acceptTerms(terms.version);
        showToast(`Terms of Service & Media Release acknowledged!`);
        if (onAccepted) {
          onAccepted(terms.version);
        } else {
          onClose();
        }
      } catch (err: any) {
        showToast(err.message || 'Error saving terms acceptance.', 'warning');
        updateSubmitState();
        submitBtn.innerHTML = '<i class="fa-solid fa-check"></i> Agree & Continue';
      }
    });
  }
}
